import type { ReactNode } from 'react'
import { cn } from '~/shared/lib/cn'
import { formatTimeDefault } from '~/shared/lib/datetime'
import { highlightText } from '~/shared/lib/highlight-text'
import { LocalIcon, Text } from '~/shared/ui'
import type { BaseEventProps } from '../../model'

interface EventShellProps {
  event: BaseEventProps
  searchQuery: string
  isFirstInFirstGroup: boolean
  isLastInLastGroup: boolean
  children?: ReactNode
}

/** Timeline dot + line, timestamp, user badge, and title. Wraps event-specific content via children. */
export function EventShell({
  event,
  searchQuery,
  isFirstInFirstGroup,
  isLastInLastGroup,
  children,
}: EventShellProps) {
  return (
    <div className="flex gap-1">
      <div className="relative w-10 shrink-0">
        <div
          className={cn(
            'bg-content-low-emphasis absolute top-0 left-1/2 h-full w-0.5 -translate-x-1/2',
            {
              'top-1 h-[calc(100%-4px)]': isFirstInFirstGroup,
              'h-1': isLastInLastGroup,
            },
          )}
        />
        <div className="bg-content-medium-emphasis-no-alpha absolute top-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full" />
      </div>

      <div className="flex-1 pb-4">
        <div className="py-0.5">
          <div className="flex items-center gap-2">
            <Text
              className="text-text-secondary uppercase"
              appearance="CAPTION"
            >
              {formatTimeDefault(event.timestamp)}
            </Text>
            <div className="flex items-center gap-1">
              <LocalIcon
                className="text-text-secondary"
                name={event.userRole === 'opti' ? 'opti' : 'personOutlined'}
                size="16"
              />
              <Text appearance="CAPTION">
                {event.userRole === 'opti'
                  ? highlightText('opti', searchQuery)
                  : highlightText(
                      [event.user?.first_name, event.user?.last_name].join(' '),
                      searchQuery,
                    )}
              </Text>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}
